// Is the location's HUB (ESP32BLEGATEWAY) live? One answer, SHARED by every
// screen and card that shows BLE lamps — each card asking for itself would be
// one MESHPING per card per minute.
//
// Live = the hub's server heartbeat is recent (it posts UpdateDeviceNetworkDetailsNew
// every 3 min), or it answered a real command. No MQTT traffic is spent on it.
//
//   watchHub(uno, userId, fn)  → fn({ live, ready, lastLiveAt }) now and on
//                                every change. Returns unsubscribe.
//   noteHub(uno, live)         → HubTransport reports every real reply/timeout.
//
// ⚠️ NO MESHPING IN NORMAL USE (2026-10-12). This used to MESHPING each hub
// every 60 s while any card watched it — per open app, so every phone and
// browser tab added a ping a minute, forever, for a badge. Now "live" comes
// from the server heartbeat (one cached REST read, shared with the device
// lists) when a screen first watches the hub and when the app returns to the
// foreground; every real command is evidence too (noteHub). The UI only blocks
// on live === false / ready === false, so an unknown `ready` costs nothing.
// MESHPING (which also tells `ready`) survives only as a debug switch:
//   localStorage.setItem('smartify.hubPingDebug', '1')   // then reload
//
// `ready` = the hub has this location's mesh keys (MESHNET:<addr>,… rather than
// MESHNET:NONE) — a live hub without keys still cannot drive a lamp.

import { getLastSeenCached } from '../../devices/deviceStatus';

const CHECK_EVERY_MS = 60000; // debug only
// The hub posts its heartbeat every 3 min; two missed in a row = offline.
const HEARTBEAT_LIVE_MS = 6 * 60 * 1000;
const STALE_MS = 50000;

function pollInDebug() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('smartify.hubPingDebug') === '1';
  } catch (e) {
    return false;
  }
}

// Back in the foreground: re-ask each watched hub whose answer is stale. One
// listener for the module, installed on the first watch.
const lastUser = new Map(); // uno -> userId, for the foreground re-check
let foregroundHooked = false;
function hookForeground() {
  if (foregroundHooked || typeof document === 'undefined') return;
  foregroundHooked = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    subs.forEach((set, uno) => {
      // eslint-disable-next-line no-use-before-define
      if (set.size && Date.now() - get(uno).checkedAt > STALE_MS) check(uno, lastUser.get(uno));
    });
  });
}
const state = new Map(); // uno -> { live, ready, lastLiveAt, checkedAt }
const subs = new Map(); // uno -> Set<fn>
const timers = new Map(); // uno -> interval
const pending = new Map(); // uno -> promise

const get = (uno) => state.get(uno) || {
  live: undefined, ready: undefined, lastLiveAt: null, checkedAt: 0,
};

// Told about EVERY hub change and every node → hub learned (useOnlineOnly).
const anyListeners = new Set();
const notifyAny = () => anyListeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
export function onAnyHubChange(fn) {
  anyListeners.add(fn);
  return () => { anyListeners.delete(fn); };
}

function publish(uno, patch) {
  const next = { ...get(uno), ...patch };
  state.set(uno, next);
  (subs.get(uno) || []).forEach((fn) => { try { fn(next); } catch (e) { /* a watcher must not break the rest */ } });
  notifyAny();
}

export const getHubStatus = (uno) => get(String(uno || ''));

// Which hub drives which mesh node (group / parent) — filled by useBleNode once
// it knows, so "Hide offline" (core/hooks/useHideOffline) can hide a group whose
// card says "Hub offline" without a lookup of its own. Mesh nodes only.
const nodeHub = new Map(); // node uno -> hub uno
export function noteNodeHub(nodeUno, hubUno) {
  const n = String(nodeUno || '');
  if (!n) return;
  const h = hubUno ? String(hubUno) : null;
  if ((nodeHub.get(n) || null) === h) return;
  if (h) nodeHub.set(n, h); else nodeHub.delete(n);
  notifyAny();
}
export const hubOfNode = (nodeUno) => nodeHub.get(String(nodeUno || '')) || null;

/** Every real exchange with the hub is evidence: a reply = live, a timeout = not. */
export function noteHub(uno, live, ready) {
  const u = String(uno || '');
  if (!u) return;
  const patch = { live, checkedAt: Date.now() };
  if (live) patch.lastLiveAt = Date.now();
  if (ready !== undefined) patch.ready = ready;
  publish(u, patch);
}

async function check(uno, userId) {
  if (pending.has(uno)) return pending.get(uno);
  const p = (async () => {
    if (!pollInDebug()) {
      // Normal use: the server heartbeat, no MQTT at all.
      const age = await getLastSeenCached(uno).catch(() => null);
      const live = age != null && age < HEARTBEAT_LIVE_MS;
      const patch = { live, checkedAt: Date.now() };
      if (age != null) patch.lastLiveAt = Date.now() - age;
      publish(uno, patch);
      return;
    }
    // Debug: ask the hub itself. Late import: hubTransport imports this module.
    const { HubTransport } = await import('./hubTransport');
    try {
      // send() itself reports live / not live through noteHub.
      const body = await new HubTransport(uno, userId).send('MESHPING', { timeoutMs: 3000 });
      noteHub(uno, true, !/MESHNET:NONE/i.test(body));
    } catch (e) {
      // "No internet" from waitForConnect never reached the hub: say nothing
      // about it beyond not-live, the screen shows its own offline banner.
      if (get(uno).live !== false) noteHub(uno, false);
      // Never heard in this session: the server heartbeat says since when.
      if (!get(uno).lastLiveAt) {
        const age = await getLastSeenCached(uno).catch(() => null);
        if (age != null) publish(uno, { lastLiveAt: Date.now() - age });
      }
    }
  })().finally(() => pending.delete(uno));
  pending.set(uno, p);
  return p;
}

export function watchHub(uno, userId, fn) {
  const u = String(uno || '');
  if (!u) return () => {};
  if (!subs.has(u)) subs.set(u, new Set());
  subs.get(u).add(fn);
  lastUser.set(u, userId);
  hookForeground();
  fn(get(u));
  if (Date.now() - get(u).checkedAt > STALE_MS) check(u, userId);
  if (pollInDebug() && !timers.has(u)) timers.set(u, setInterval(() => check(u, userId), CHECK_EVERY_MS));
  return () => {
    const set = subs.get(u);
    if (set) set.delete(fn);
    if (set && set.size === 0) {
      clearInterval(timers.get(u));
      timers.delete(u);
    }
  };
}

/** "since 4:20 PM" / "since 11 Oct, 4:20 PM" / "" */
export function offlineSinceText(lastLiveAt, now = Date.now()) {
  if (!lastLiveAt) return '';
  const d = new Date(lastLiveAt);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const sameDay = new Date(now).toDateString() === d.toDateString();
  return sameDay ? `since ${time}` : `since ${d.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
}
